import axios from 'axios';
import { Catalog } from './search-catalog';

const apiUrl = process.env.BACKEND_URL;

export type SEARCH_OPTIONS = {
  groups: string[];
  tags: string[];
  platforms: string[];
  slugs: string[];
  category: string[];
  departmentSlug: string | string[];
  departmentSection: string;
  query: string;
  top_hit: number;
  highlighted: true;
  isDigital: boolean;
  isOnline: boolean;
  from: number;
  size: number;
  hasLink?: boolean;
};

export type GetMostAccessedCatalogsField =
  | 'title'
  | 'tags'
  | 'description'
  | 'category'
  | 'department';

export interface GetMostAccessedCatalogsParams {
  field: GetMostAccessedCatalogsField;
  q: string;
  limit?: number;
  offset?: number;
  isDigital?: boolean;
  isOnline?: boolean;
  hasLink?: boolean;
}

type GetSearchCatalogCountParams = Pick<
  GetMostAccessedCatalogsParams,
  'q' | 'isDigital' | 'isOnline' | 'hasLink'
>;

type GetMostAccessedCatalogsRequestParams = {
  field: GetMostAccessedCatalogsField;
  q: string;
  limit?: number;
  offset?: number;
  isDigital?: boolean;
  isOnline?: boolean;
  hasLink?: boolean;
};

export async function getRelevant(accessToken?: string): Promise<Catalog[]> {
  const response = await axios.post(
    `${apiUrl}/v1/search`,
    {
      groups: ['CATALOG'],
      highlighted: true,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response.data;
}

export async function getTopCatalog(accessToken?: string): Promise<Catalog[]> {
  const response = await axios.post(
    `${apiUrl}/v1/search`,
    {
      top_hit: 4,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response.data;
}

export async function getCatalog(
  accessToken?: string,
  searchOptions?: Partial<SEARCH_OPTIONS>
): Promise<{ items: Catalog[]; totalPages: number }> {
  const from = searchOptions?.from || 0;
  const size = searchOptions?.size || 10;
  const { data } = await axios.post(
    `${apiUrl}/v1/search`,
    {
      groups: ['CATALOG'],
      from,
      size,
      ...searchOptions,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  const totalItems = data[0]?.resultTotal || 0;
  const totalPages = Math.ceil(totalItems / size);

  if (!searchOptions?.departmentSlug && typeof window !== 'undefined') {
    window.localStorage.removeItem('selectedDepartmentData');
  }

  return {
    items: data,
    totalPages,
  };
}

export async function getMostAccessedCatalogs({
  field,
  q,
  limit,
  offset,
  isDigital,
  isOnline,
  hasLink,
}: GetMostAccessedCatalogsParams): Promise<Catalog[]> {
  if (!q) {
    return [];
  }

  const params: GetMostAccessedCatalogsRequestParams = {
    field,
    q,
  };

  if (limit !== undefined) {
    params.limit = limit;
  }

  if (offset !== undefined) {
    params.offset = offset;
  }

  if (isDigital !== undefined) {
    params.isDigital = isDigital;
  }

  if (isOnline !== undefined) {
    params.isOnline = isOnline;
  }

  if (hasLink !== undefined) {
    params.hasLink = hasLink;
  }

  const { data } = await axios.get(
    `${apiUrl}/v1/search/catalogs/most-accessed`,
    {
      params,
      headers: {
        accept: 'application/json',
      },
    }
  );

  return data || [];
}

export async function getSearchCatalogCount({
  q,
  isDigital,
  isOnline,
  hasLink,
}: GetSearchCatalogCountParams): Promise<number> {
  if (!q) {
    return 0;
  }

  const params: GetSearchCatalogCountParams = {
    q,
  };

  if (isDigital !== undefined) {
    params.isDigital = isDigital;
  }

  if (isOnline !== undefined) {
    params.isOnline = isOnline;
  }

  if (hasLink !== undefined) {
    params.hasLink = hasLink;
  }

  const { data } = await axios.get(`${apiUrl}/v1/search/catalogs/count`, {
    params,
    headers: {
      accept: 'application/json',
    },
  });

  return data?.count || 0;
}
